import React, { useState, useEffect } from "react";
import styles from "./task-list-header.module.css";

// Changes:
// Added extra header for user value of a task
// Changed header names to Polish

export const TaskListHeaderDefault: React.FC<{
  nameColumnWidth: number;
  headerHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  columnVisibility?: {
    coordinator?: boolean;
    executor?: boolean;
    branch?: boolean;
    name?: boolean;
    start?: boolean;
    end?: boolean;
    progress?: boolean;
  };
  columnOrder?: string[];
  onColumnOrderChange?: (newOrder: string[]) => void;
  columnWidths?: Record<string, number>;
  onColumnWidthsChange?: (widths: Record<string, number>, shouldSave?: boolean) => void;
}> = ({ nameColumnWidth, headerHeight, fontFamily, fontSize, rowWidth, columnVisibility, columnOrder, onColumnOrderChange, columnWidths, onColumnWidthsChange }) => {
  const visibility = columnVisibility || {
    coordinator: true,
    executor: true,
    branch: true,
    name: true,
    start: true,
    end: true,
    progress: true
  };

  const defaultOrder = ['coordinator', 'executor', 'branch', 'name', 'start', 'end', 'progress'];
  const order = columnOrder || defaultOrder;

  const defaultWidths: Record<string, number> = {
    coordinator: parseFloat(rowWidth),
    executor: parseFloat(rowWidth),
    branch: parseFloat(rowWidth),
    name: nameColumnWidth,
    start: parseFloat(rowWidth),
    end: parseFloat(rowWidth),
    progress: parseFloat(rowWidth),
  };

  const widths = columnWidths || defaultWidths;

  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [startX, setStartX] = useState<number>(0);
  const [startWidth, setStartWidth] = useState<number>(0);

  const columnLabels: Record<string, string> = {
    coordinator: 'Koordynator',
    executor: 'Wykonawca',
    branch: 'Branża',
    name: 'Nazwa',
    start: 'Początek',
    end: 'Koniec',
    progress: 'Postęp'
  };

  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    
    if (!draggedColumn || draggedColumn === targetColumnId) {
      setDraggedColumn(null);
      setDragOverColumn(null);
      return;
    }

    const newOrder = [...order];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnId);

    // Remove dragged column and insert at target position
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);

    if (onColumnOrderChange) {
      onColumnOrderChange(newOrder);
    }

    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleResizeStart = (e: React.MouseEvent, columnId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnId);
    setStartX(e.clientX);
    const initialWidth = widths[columnId] || parseFloat(rowWidth);
    setStartWidth(initialWidth);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (resizingColumn) {
        const diff = e.clientX - startX;
        const newWidth = Math.max(50, startWidth + diff); // Minimum width of 50px
        const newWidths = { ...widths, [resizingColumn]: newWidth };
        if (onColumnWidthsChange) {
          onColumnWidthsChange(newWidths, false); // Update view but don't save
        }
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (resizingColumn) {
        const finalDiff = e.clientX - startX;
        const finalWidth = Math.max(50, startWidth + finalDiff);
        const newWidths = { ...widths, [resizingColumn]: finalWidth };
        if (onColumnWidthsChange) {
          onColumnWidthsChange(newWidths, true); // Save on mouseup
        }
      }
      setResizingColumn(null);
    };

    if (resizingColumn) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn, startX, startWidth, widths, onColumnWidthsChange]);

  const renderColumn = (columnId: string) => {
    if (!visibility[columnId as keyof typeof visibility]) {
      return null;
    }

    const isDragging = draggedColumn === columnId;
    const isDragOver = dragOverColumn === columnId;
    const isResizing = resizingColumn === columnId;
    const width = widths[columnId] || (columnId === 'name' ? nameColumnWidth : parseFloat(rowWidth));

    return (
      <React.Fragment key={columnId}>
        <div
          className={styles.ganttTable_HeaderItem}
          style={{
            minWidth: width,
            maxWidth: width,
            width: width,
            cursor: 'move',
            opacity: isDragging ? 0.5 : 1,
            backgroundColor: isDragOver ? '#e0e0e0' : undefined,
            position: 'relative',
            userSelect: 'none',
          }}
          draggable
          onDragStart={(e) => handleDragStart(e, columnId)}
          onDragOver={(e) => handleDragOver(e, columnId)}
          onDrop={(e) => handleDrop(e, columnId)}
          onDragEnd={handleDragEnd}
        >
          &nbsp;{columnLabels[columnId]}
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '5px',
              cursor: 'col-resize',
              zIndex: 10,
              backgroundColor: isResizing ? '#2196F3' : 'transparent',
            }}
            onMouseDown={(e) => handleResizeStart(e, columnId)}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.backgroundColor = '#2196F3';
            }}
            onMouseLeave={(e) => {
              if (!isResizing) {
                (e.target as HTMLElement).style.backgroundColor = 'transparent';
              }
            }}
          />
        </div>
        <div
          className={styles.ganttTable_HeaderSeparator}
          style={{
            height: headerHeight * 0.5,
            marginTop: headerHeight * 0.2,
          }}
        />
      </React.Fragment>
    );
  };
  
  return (

    <div
      className={styles.ganttTable}
      style={{
        fontFamily: fontFamily,
        fontSize: fontSize,
      }}
    >
      <div
        className={styles.ganttTable_Header}
        style={{
          height: headerHeight - 2,
        }}
      >
        {order.map((columnId) => renderColumn(columnId))}
      </div>
    </div>
  );
};
